import type {
  PermissionType,
  PermissionMetadata,
  PermissionResult
} from '../types'
import { BasePermissionStrategy } from './base.strategy'
import { Service } from '@/services/helpers/ServiceHelper'

export class MicrophoneStrategy extends BasePermissionStrategy {
  readonly type: PermissionType = 'microphone'

  readonly metadata: PermissionMetadata = {
    type: 'microphone',
    title: 'Microphone Permission Required',
    description:
        'We need access to your microphone to manage calls and audio input. Please allow microphone access when prompted.',
    deniedTitle: 'Microphone Permission Blocked',
    deniedDescription:
        "You've blocked microphone access. To manage calls and audio input, please enable microphone access manually in your device settings."
  }

  isSupported (): boolean {
    if (this.context.isMobile) {
      return true
    }
    return (
      typeof navigator !== 'undefined' &&
        'mediaDevices' in navigator &&
        'getUserMedia' in navigator.mediaDevices
    )
  }

  async check (): Promise<PermissionResult> {
    if (!this.isSupported()) {
      return this.createResult('unsupported')
    }

    if (this.context.isMobile) {
      return await this.checkMobile()
    }
    return await this.checkWeb()
  }

  async request (): Promise<PermissionResult> {
    if (!this.isSupported()) {
      return this.createResult('unsupported')
    }

    if (this.context.isMobile) {
      return await this.requestMobile()
    }
    return await this.requestWeb()
  }

  // === WEB ===
  private async checkWeb (): Promise<PermissionResult> {
    try {
      const permission = await navigator.permissions.query({
        name: 'microphone' as PermissionName
      })
      return this.createResult(
          permission.state as 'granted' | 'denied' | 'prompt'
      )
    } catch {
      return this.createResult('prompt')
    }
  }

  private async requestWeb (): Promise<PermissionResult> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach(track => track.stop())
      return this.createResult('granted')
    } catch (error: any) {
      if (
        error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError'
      ) {
        return this.createResult('denied')
      }
      return this.createResult('prompt')
    }
  }

  // === MOBILE ===
  private async checkMobile (): Promise<PermissionResult> {
    try {
      const result =
          await Service.capacitor.permissionsCapacitor?.checkPermission(
            'microphone'
          )
      if (!result) {
        return this.createResult('unsupported')
      }
      return this.createResult(result.state)
    } catch {
      return this.createResult('unsupported')
    }
  }

  private async requestMobile (): Promise<PermissionResult> {
    try {
      const result =
          await Service.capacitor.permissionsCapacitor?.requestPermission(
            'microphone'
          )
      if (!result) {
        return this.createResult('unsupported')
      }
      return this.createResult(result.state)
    } catch {
      return this.createResult('denied')
    }
  }
}
